export function relativeTime(date) {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff} seconds ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m} minute${m > 1 ? "s" : ""} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const days = Math.floor(h / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

// `offsetMinutes` is a fixed UTC offset (e.g. -300 for "-0500"), not an
// IANA zone — Bouncie tags every trip with the literal offset that was in
// effect for the vehicle at that moment (its own web app displays trip
// times using exactly that), so reproducing it here means applying the
// same fixed shift rather than looking up a browser-local or IANA zone.
// When a trip has no offset yet (e.g. before its first REST
// reconciliation), these fall back to the viewing browser's own zone —
// the best available guess, not a fabricated one.
function zonedParts(date, offsetMinutes) {
  const d = date instanceof Date ? date : new Date(date);
  if (offsetMinutes == null) {
    return {
      h: d.getHours(), m: d.getMinutes(),
      y: d.getFullYear(), mo: d.getMonth(), day: d.getDate(),
      weekday: d.toLocaleDateString([], { weekday: "long" }),
      monthShort: d.toLocaleDateString([], { month: "short" }),
    };
  }
  const shifted = new Date(d.getTime() + offsetMinutes * 60000);
  const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return {
    h: shifted.getUTCHours(), m: shifted.getUTCMinutes(),
    y: shifted.getUTCFullYear(), mo: shifted.getUTCMonth(), day: shifted.getUTCDate(),
    weekday: WEEKDAYS[shifted.getUTCDay()],
    monthShort: MONTHS[shifted.getUTCMonth()],
  };
}

// Stable grouping key for "same calendar day" bucketing (e.g. the Trips
// page's date-sectioned list) — computed in the same zone as
// clockTime/shortDate/dateSectionLabel so a trip is grouped under the day
// it actually displays under, not whatever day its raw UTC instant falls
// on in the viewer's browser.
export function zonedDateKey(date, offsetMinutes) {
  if (!date) return "unknown";
  const { y, mo, day } = zonedParts(date, offsetMinutes);
  return `${y}-${mo}-${day}`;
}

export function clockTime(date, offsetMinutes) {
  if (!date) return "—";
  const { h, m } = zonedParts(date, offsetMinutes);
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

export function shortDate(date, offsetMinutes) {
  if (!date) return "—";
  const { monthShort, day } = zonedParts(date, offsetMinutes);
  return `${monthShort} ${day}`;
}

// Absolute date + wall-clock time, for events where "3 days ago" isn't
// enough — an alert log is read to find out *when* something happened, and a
// relative age can't be compared against a driver's shift or a delivery
// window. The year is appended only when it isn't the current one, so the
// common case stays short.
export function dateTime(date, offsetMinutes) {
  if (!date) return "—";
  const { y, monthShort, day } = zonedParts(date, offsetMinutes);
  const year = y === new Date().getFullYear() ? "" : ` ${y}`;
  return `${monthShort} ${day}${year}, ${clockTime(date, offsetMinutes)}`;
}

// Section-header label for a date-grouped list (e.g. the Trips page) —
// "Today"/"Yesterday" for the two most recent days, then a full weekday
// date beyond that. "Today"/"Yesterday" are judged against the browser's
// own current date even when offsetMinutes is given — there's no
// "current time at the vehicle" to compare against other than now, and a
// stale/future device clock isn't a case worth designing around.
export function dateSectionLabel(date, offsetMinutes) {
  if (!date) return "Unknown date";
  const { y, mo, day, weekday, monthShort } = zonedParts(date, offsetMinutes);
  const zoned = new Date(y, mo, day);
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(zoned)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return `${weekday}, ${monthShort} ${day}`;
}

export function fmtDuration(minutes) {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

// Bouncie's REST/webhook payloads report distance/speed/fuel in miles,
// mph and gallons (it's a US-only product) — confirmed by cross-checking
// a real trip's stored distance against the same trip in Bouncie's own
// dashboard, which showed the identical number labeled "mi". These values
// are stored and passed around unconverted (fields still named distance_km
// etc. — renaming every internal key was a much bigger, riskier change for
// no user-visible benefit), so only the display suffix changes here.
export const fmtKm = (n) => (n == null ? "—" : `${n.toFixed(n < 100 ? 1 : 0)} mi`);
export const fmtSpeed = (n) => (n == null ? "—" : `${Math.round(n)} mph`);
export const fmtL = (n) => (n == null ? "—" : `${n.toFixed(1)} gal`);
export const fmtPct = (n) => (n == null ? "—" : `${Math.round(n)}%`);
// Odometer readings run to five or six figures, so they get thousands
// separators — 45,691 is readable at a glance where 45691 is not. One decimal
// because Bouncie reports tenths and dropping them would make a short trip's
// start and end look identical.
export const fmtOdometer = (n) =>
  n == null ? "—" : `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mi`;
export const fmtNum = (n) => (n == null ? "—" : n.toLocaleString());

export function initials(name) {
  return (name || "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Compass direction for a heading in degrees.
//
// Eight points, not sixteen: the UI names the direction next to the exact
// degrees, so "Northeast" is there to be read at a glance while the number
// carries the precision. Sixteen points would add "North-northeast", which is
// harder to scan and no more useful when the degrees are right beside it.
//
// Bin width is 45°, and each bin is centred on its cardinal — North therefore
// spans 337.5°–22.5°, wrapping through 0. Rounding the offset index handles
// that wrap without a special case.
const COMPASS_POINTS = [
  "North", "Northeast", "East", "Southeast",
  "South", "Southwest", "West", "Northwest",
];

export function compassDirection(degrees) {
  if (degrees == null || Number.isNaN(Number(degrees))) return null;
  // Normalise first: Bouncie has been seen reporting 360, and a negative or
  // out-of-range value should still land somewhere sensible rather than
  // indexing off the end of the array.
  const normalized = ((Number(degrees) % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(normalized / 45) % 8];
}
